import React, { useState } from 'react';
import { X, Clock, Globe, Shield, Cloud, Server, Database, ChevronDown, ChevronUp, Check, Settings, Calendar, Zap, Bell } from 'lucide-react';
import type { Environment } from './RecentScans';
interface ScanConfig {
  envId: string;
  frequency: 'Daily' | 'Weekly' | 'Monthly' | 'Manual';
  scope: 'all' | 'specific';
  frameworks: string[];
  notifications: boolean;
  lastScan: string;
  nextScan: string;
}
interface ConfigureScansModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const environments: Environment[] = [{
  id: 'ENV-001',
  clientName: 'Acme Corp',
  type: 'Hybrid',
  status: 'healthy',
  identities: 12450,
  complianceScore: 94,
  overPrivileged: 12,
  lastSync: '2m ago'
}, {
  id: 'ENV-002',
  clientName: 'Globex Inc',
  type: 'AWS',
  status: 'warning',
  identities: 3840,
  complianceScore: 78,
  overPrivileged: 45,
  lastSync: '15m ago'
}, {
  id: 'ENV-003',
  clientName: 'Soylent Corp',
  type: 'On-Prem',
  status: 'critical',
  identities: 850,
  complianceScore: 62,
  overPrivileged: 128,
  lastSync: '1h ago'
}, {
  id: 'ENV-004',
  clientName: 'Umbrella Corp',
  type: 'Azure',
  status: 'healthy',
  identities: 15200,
  complianceScore: 98,
  overPrivileged: 3,
  lastSync: '5m ago',
  govCloud: true
}, {
  id: 'ENV-005',
  clientName: 'Stark Ind',
  type: 'GCP',
  status: 'warning',
  identities: 5600,
  complianceScore: 85,
  overPrivileged: 22,
  lastSync: '30m ago',
  govCloud: true
}];
const allFrameworks = ['CIS Benchmarks', 'NIST 800-53', 'NIST 800-171', 'ISO 27001', 'SOC 2', 'CMMC', 'FedRAMP', 'HIPAA'];
const initialConfigs: ScanConfig[] = [{
  envId: 'ENV-001',
  frequency: 'Weekly',
  scope: 'all',
  frameworks: ['CIS Benchmarks', 'SOC 2'],
  notifications: true,
  lastScan: 'Feb 12, 10:32 AM',
  nextScan: 'Feb 19, 10:00 AM'
}, {
  envId: 'ENV-002',
  frequency: 'Daily',
  scope: 'all',
  frameworks: ['CIS Benchmarks', 'NIST 800-53', 'SOC 2'],
  notifications: true,
  lastScan: 'Feb 12, 6:00 AM',
  nextScan: 'Feb 13, 6:00 AM'
}, {
  envId: 'ENV-003',
  frequency: 'Daily',
  scope: 'specific',
  frameworks: ['CIS Benchmarks', 'NIST 800-53', 'ISO 27001'],
  notifications: true,
  lastScan: 'Feb 12, 3:15 AM',
  nextScan: 'Feb 13, 3:00 AM'
}, {
  envId: 'ENV-004',
  frequency: 'Weekly',
  scope: 'all',
  frameworks: ['CIS Benchmarks', 'NIST 800-171', 'CMMC', 'FedRAMP'],
  notifications: true,
  lastScan: 'Feb 10, 11:00 PM',
  nextScan: 'Feb 17, 11:00 PM'
}, {
  envId: 'ENV-005',
  frequency: 'Weekly',
  scope: 'all',
  frameworks: ['CIS Benchmarks', 'NIST 800-171', 'CMMC', 'FedRAMP'],
  notifications: false,
  lastScan: 'Feb 11, 8:45 AM',
  nextScan: 'Feb 18, 8:00 AM'
}];
export function ConfigureScansModal({
  isOpen,
  onClose
}: ConfigureScansModalProps) {
  const [configs, setConfigs] = useState<ScanConfig[]>(initialConfigs);
  const [expandedEnv, setExpandedEnv] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  if (!isOpen) return null;
  const getTypeIcon = (type: Environment['type']) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={12} data-id="element-117" />;
      case 'On-Prem':
        return <Server size={12} data-id="element-118" />;
      case 'Hybrid':
        return <Database size={12} data-id="element-119" />;
      default:
        return <Server size={12} data-id="element-120" />;
    }
  };
  const getStatusColor = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-astro-green';
      case 'warning':
        return 'bg-yellow-400';
      case 'critical':
        return 'bg-astro-danger';
      default:
        return 'bg-astro-text-secondary';
    }
  };
  const getFrequencyColor = (freq: string) => {
    switch (freq) {
      case 'Daily':
        return 'text-astro-green bg-astro-green/10 border-astro-green/20';
      case 'Weekly':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
      case 'Monthly':
        return 'text-astro-text-secondary bg-white/5 border-white/10';
      case 'Manual':
        return 'text-astro-text-secondary/50 bg-white/[0.02] border-white/5';
      default:
        return 'text-astro-text-secondary bg-white/5 border-white/10';
    }
  };
  const updateConfig = (envId: string, updates: Partial<ScanConfig>) => {
    setConfigs(prev => prev.map(c => c.envId === envId ? {
      ...c,
      ...updates
    } : c));
  };
  const toggleFramework = (envId: string, framework: string) => {
    setConfigs(prev => prev.map(c => {
      if (c.envId !== envId) return c;
      const has = c.frameworks.includes(framework);
      return {
        ...c,
        frameworks: has ? c.frameworks.filter(f => f !== framework) : [...c.frameworks, framework]
      };
    }));
  };
  const handleSave = (envId: string) => {
    setSaved(envId);
    setTimeout(() => setSaved(null), 1500);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-121">
      <div className="w-full max-w-[680px] max-h-[85vh] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col" data-id="element-122">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-astro-border shrink-0" data-id="element-123">
          <div data-id="element-124">
            <h2 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-125">
              <Settings size={18} className="text-astro-green" data-id="element-126" />
              Configure Scans
            </h2>
            <p className="text-xs text-astro-text-secondary mt-0.5" data-id="element-127">
              Per-environment scan schedules and compliance frameworks
            </p>
          </div>
          <button onClick={onClose} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-128">

            <X size={20} data-id="element-129" />
          </button>
        </div>

        {/* Summary Row */}
        <div className="px-5 py-3 border-b border-astro-border bg-[#0a0e14] flex items-center gap-4 shrink-0" data-id="element-130">
          <div className="flex items-center gap-1.5" data-id="element-131">
            <Zap size={12} className="text-astro-green" data-id="element-132" />
            <span className="text-[11px] text-astro-text-secondary" data-id="element-133">
              <span className="text-white font-bold" data-id="element-134">
                {configs.filter(c => c.frequency === 'Daily').length}
              </span>{' '}
              daily
            </span>
          </div>
          <span className="text-astro-border" data-id="element-135">·</span>
          <div className="flex items-center gap-1.5" data-id="element-136">
            <Clock size={12} className="text-blue-400" data-id="element-137" />
            <span className="text-[11px] text-astro-text-secondary" data-id="element-138">
              <span className="text-white font-bold" data-id="element-139">
                {configs.filter(c => c.frequency === 'Weekly').length}
              </span>{' '}
              weekly
            </span>
          </div>
          <span className="text-astro-border" data-id="element-140">·</span>
          <div className="flex items-center gap-1.5" data-id="element-141">
            <Calendar size={12} className="text-astro-text-secondary" data-id="element-142" />
            <span className="text-[11px] text-astro-text-secondary" data-id="element-143">
              <span className="text-white font-bold" data-id="element-144">
                {configs.filter(c => c.frequency === 'Monthly').length}
              </span>{' '}
              monthly
            </span>
          </div>
          <div className="ml-auto flex items-center gap-1.5" data-id="element-145">
            <Bell size={12} className="text-astro-text-secondary" data-id="element-146" />
            <span className="text-[11px] text-astro-text-secondary" data-id="element-147">
              <span className="text-white font-bold" data-id="element-148">
                {configs.filter(c => c.notifications).length}
              </span>
              /{environments.length} alerts on
            </span>
          </div>
        </div>

        {/* Environment List */}
        <div className="flex-1 overflow-y-auto" data-id="element-149">
          <div className="divide-y divide-astro-border" data-id="element-150">
            {environments.map(env => {
            const config = configs.find(c => c.envId === env.id)!;
            const isExpanded = expandedEnv === env.id;
            const isSaved = saved === env.id;
            return <div key={env.id} data-id="element-151">
                  {/* Environment Row */}
                  <button onClick={() => setExpandedEnv(isExpanded ? null : env.id)} className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors ${isExpanded ? 'bg-white/[0.02]' : 'hover:bg-white/[0.02]'}`} data-id="element-152">

                    <div className={`w-2 h-2 rounded-full shrink-0 ${getStatusColor(env.status)}`} data-id="element-153">
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-154">
                      <div className="flex items-center gap-2" data-id="element-155">
                        <span className="text-sm font-medium text-white" data-id="element-156">
                          {env.clientName}
                        </span>
                        <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-astro-text-secondary/60" data-id="element-157">
                          {getTypeIcon(env.type)}
                          {env.type}
                        </span>
                        {env.govCloud && <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/25" data-id="element-158">
                            GOV
                          </span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5" data-id="element-159">
                        <span className="text-[10px] text-astro-text-secondary" data-id="element-160">
                          Last: {config.lastScan}
                        </span>
                        <span className="text-astro-border text-[10px]" data-id="element-161">·</span>
                        <span className="text-[10px] text-astro-text-secondary" data-id="element-162">
                          Next: {config.nextScan}
                        </span>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border shrink-0 ${getFrequencyColor(config.frequency)}`} data-id="element-163">

                      {config.frequency}
                    </span>
                    <span className="text-[10px] text-astro-text-secondary shrink-0" data-id="element-164">
                      {config.frameworks.length} frameworks
                    </span>
                    {isExpanded ? <ChevronUp size={14} className="text-astro-text-secondary shrink-0" data-id="element-165" /> : <ChevronDown size={14} className="text-astro-text-secondary shrink-0" data-id="element-166" />}
                  </button>

                  {/* Expanded Config */}
                  {isExpanded && <div className="px-5 pb-4 pt-1 bg-[#0a0e14]/50 animate-in fade-in slide-in-from-top-2 duration-150" data-id="element-167">
                      <div className="ml-5 space-y-4" data-id="element-168">
                        {/* Frequency */}
                        <div data-id="element-169">
                          <label className="block text-[10px] font-bold text-astro-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-170">
                            <Clock size={10} className="text-astro-green" data-id="element-171" />
                            Scan Frequency
                          </label>
                          <div className="grid grid-cols-4 gap-2" data-id="element-172">
                            {(['Daily', 'Weekly', 'Monthly', 'Manual'] as const).map(freq => <button key={freq} onClick={() => updateConfig(env.id, {
                        frequency: freq
                      })} className={`px-3 py-2 text-xs font-bold rounded border transition-colors ${config.frequency === freq ? 'bg-astro-green/10 border-astro-green/30 text-astro-green' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-173">

                                {freq}
                              </button>)}
                          </div>
                        </div>

                        {/* Scope */}
                        <div data-id="element-174">
                          <label className="block text-[10px] font-bold text-astro-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-175">
                            <Globe size={10} className="text-astro-green" data-id="element-176" />
                            Scan Scope
                          </label>
                          <div className="flex gap-3" data-id="element-177">
                            <button onClick={() => updateConfig(env.id, {
                        scope: 'all'
                      })} className={`flex items-center gap-2 px-3 py-2 rounded border text-xs font-medium transition-colors ${config.scope === 'all' ? 'bg-astro-green/10 border-astro-green/30 text-astro-green' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white'}`} data-id="element-178">

                              {config.scope === 'all' && <Check size={12} data-id="element-179" />}
                              All Resources
                            </button>
                            <button onClick={() => updateConfig(env.id, {
                        scope: 'specific'
                      })} className={`flex items-center gap-2 px-3 py-2 rounded border text-xs font-medium transition-colors ${config.scope === 'specific' ? 'bg-astro-green/10 border-astro-green/30 text-astro-green' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white'}`} data-id="element-180">

                              {config.scope === 'specific' && <Check size={12} data-id="element-181" />}
                              Specific Groups
                            </button>
                          </div>
                        </div>

                        {/* Frameworks */}
                        <div data-id="element-182">
                          <label className="block text-[10px] font-bold text-astro-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-183">
                            <Shield size={10} className="text-astro-green" data-id="element-184" />
                            Compliance Frameworks
                          </label>
                          <div className="flex flex-wrap gap-2" data-id="element-185">
                            {allFrameworks.map(fw => {
                        const active = config.frameworks.includes(fw);
                        return <button key={fw} onClick={() => toggleFramework(env.id, fw)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[11px] font-medium transition-colors ${active ? 'bg-astro-green/10 border-astro-green/25 text-astro-green' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary/50 hover:text-astro-text-secondary hover:border-white/10'}`} data-id="element-186">

                                  {active && <Check size={10} data-id="element-187" />}
                                  {fw}
                                </button>;
                      })}
                          </div>
                        </div>

                        {/* Notifications Toggle */}
                        <div className="flex items-center justify-between p-3 bg-astro-card border border-astro-border rounded-lg" data-id="element-188">
                          <div className="flex items-center gap-2" data-id="element-189">
                            <Bell size={14} className={config.notifications ? 'text-astro-green' : 'text-astro-text-secondary/40'} data-id="element-190" />

                            <div data-id="element-191">
                              <span className="text-xs font-medium text-white block" data-id="element-192">
                                Scan Notifications
                              </span>
                              <span className="text-[10px] text-astro-text-secondary" data-id="element-193">
                                Email alerts on scan completion and new findings
                              </span>
                            </div>
                          </div>
                          <button onClick={() => updateConfig(env.id, {
                      notifications: !config.notifications
                    })} className={`relative w-9 h-5 rounded-full transition-colors ${config.notifications ? 'bg-astro-green' : 'bg-astro-border'}`} data-id="element-194">

                            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${config.notifications ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-195">
                          </div>
                          </button>
                        </div>

                        {/* Save */}
                        <div className="flex items-center justify-end pt-1" data-id="element-196">
                          {isSaved ? <span className="flex items-center gap-1.5 text-xs font-bold text-astro-green" data-id="element-197">
                              <Check size={14} data-id="element-198" /> Saved
                            </span> : <button onClick={() => handleSave(env.id)} className="px-4 py-2 text-xs font-bold text-black bg-astro-green hover:bg-astro-green/90 rounded transition-colors shadow-[0_0_10px_rgba(34,197,94,0.15)]" data-id="element-199">

                              Save for {env.clientName}
                            </button>}
                        </div>
                      </div>
                    </div>}
                </div>;
          })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-astro-border shrink-0 bg-astro-card" data-id="element-200">
          <span className="text-[10px] text-astro-text-secondary flex items-center gap-1" data-id="element-201">
            <Shield size={10} className="text-astro-green" data-id="element-202" />
            Changes apply to the next scheduled scan
          </span>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-203">

            Close
          </button>
        </div>
      </div>
    </div>;
}